import { useCallback, useEffect, useState } from 'react';
import api from '../../hooks/useApi';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import Avatar from '../shared/Avatar';
import { LineIcon, WhereIcon } from '../shared/LineIcon';
import { CardFooter, HEADER_LINK, SECTION_HEADING } from '../shared/CardParts';

// ─── The home page's personal cards ─────────────────────────────────────────
//
// Each card asks its own endpoint under /home (src/api/routes/home.js), which
// only ever answers for the person signed in, and each stays away when it has
// nothing to say: somebody with no clearance sees no clearance tile, somebody
// with nothing in flight sees no requests card, and only a line manager sees
// their team.

function fmtDay(d, opts = { weekday: 'short', day: 'numeric', month: 'short' }) {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return new Date(+y, +m - 1, +day).toLocaleDateString('en-GB', opts);
}
const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;
const h = (n) => `${Math.round((n || 0) * 10) / 10}h`;

// Load one /home endpoint, again whenever the tab regains focus.
function useHome(path) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(path).then(setData).catch(() => setData(d => d || { failed: true }));
  }, [path]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnFocus(load);
  return data;
}

function CardShell({ title, action, children, className = '' }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-md min-w-0 ${className}`}>
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2">
        <h3 className="text-[12px] font-bold text-navy">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

// ─── Clearances, passes and certifications (a My Space tile) ────────────────
//
// The worst one leads: a lapsed clearance is red, one in its renewal window is
// amber, and when everything is in date the tile says so with the next expiry.
// The renewal itself is the PMO's (Clearance & RDN Renewals), so the tile says
// whether they have it in hand rather than sending the person anywhere.

const RENEWAL_TEXT = { raised: 'The PMO has raised the renewal.', started: 'The PMO is working on the renewal.' };

export function ComplianceCard() {
  const data = useHome('home/compliance');
  const items = data?.items || [];
  if (!items.length) return null;

  const top = items[0];
  const tone = top.state === 'lapsed' ? 'alert' : top.state === 'due' ? 'warn' : 'good';
  const box = { alert: 'border-red-200 bg-red-50', warn: 'border-amber-200 bg-amber-50', good: 'border-gray-200 bg-white' }[tone];
  const ink = { alert: 'text-red-700', warn: 'text-amber-800', good: 'text-navy' }[tone];
  const rest = items.slice(1);
  const when = top.state === 'lapsed'
    ? `Expired ${fmtDay(top.expires_on, { day: 'numeric', month: 'short', year: 'numeric' })}`
    : `Expires ${fmtDay(top.expires_on, { day: 'numeric', month: 'short', year: 'numeric' })}, in ${plural(top.days_left, 'day')}`;

  return (
    <div className={`deep-tint border rounded-md px-3 py-2.5 min-w-0 ${box}`}
      title={items.map(i => `${i.label}: ${i.state === 'lapsed' ? 'expired' : 'expires'} ${fmtDay(i.expires_on, { day: 'numeric', month: 'long', year: 'numeric' })}`).join('\n')}>
      <div className={`text-[10px] font-bold uppercase tracking-wide mb-1 ${tone === 'good' ? 'text-gray-500' : ink}`}>
        {tone === 'good' ? 'Clearances & certifications' : top.state === 'lapsed' ? 'Expired' : 'Renewal due'}
      </div>
      <div className={`text-sm font-bold leading-tight ${ink}`}>{top.label}</div>
      <div className={`text-[11px] mt-1 ${tone === 'good' ? 'text-gray-500' : ink}`}>
        {tone === 'good' ? `All in date. Next: ${when.toLowerCase()}.` : `${when}.`}
        {top.renewal && tone !== 'good' && <> {RENEWAL_TEXT[top.renewal]}</>}
        {!top.renewal && top.kind !== 'certification' && tone !== 'good' && <> Speak to the PMO or HR about renewing it.</>}
        {!top.renewal && top.kind === 'certification' && tone !== 'good' && <> Let HR know once it is renewed.</>}
      </div>
      {rest.length > 0 && tone !== 'good' && (
        <div className="text-[10px] text-gray-600 mt-1 truncate">
          Also: {rest.map(i => `${i.label}${i.state !== 'ok' ? ` (${i.state === 'lapsed' ? 'expired' : 'due'})` : ''}`).join(', ')}
        </div>
      )}
    </div>
  );
}

// ─── My requests ─────────────────────────────────────────────────────────────

const TONE = {
  wait: 'bg-amber-50 text-amber-700',
  info: 'bg-blue-50 text-blue-700',
  good: 'bg-emerald-50 text-emerald-700',
  bad: 'bg-red-50 text-red-700',
};

export function MyRequestsCard({ onNavigate, className = '' }) {
  const data = useHome('home/requests');
  const items = data?.items || [];
  if (!items.length) return null;
  const shown = items.slice(0, 6);
  return (
    <CardShell title={<>My requests <span className="text-gray-400 font-normal">({items.length})</span></>} className={className}>
      <div className="divide-y divide-gray-100">
        {shown.map(i => (
          <button key={i.key} type="button" onClick={() => onNavigate && onNavigate(i.page)}
            className="w-full text-left px-3 py-2 hover:bg-gold/5 flex items-start gap-3">
            <span className="flex-1 min-w-0">
              <span className="block text-[10px] font-bold uppercase tracking-wide text-gray-500">{i.kind}</span>
              <span className="block text-[11px] font-semibold text-navy break-words">{i.title}</span>
              {i.note && <span className="block text-[11px] text-gray-500 truncate" title={i.note}>{i.note}</span>}
            </span>
            <span className={`mt-0.5 max-w-[11rem] text-right text-[10px] leading-tight font-semibold px-1.5 py-0.5 rounded ${TONE[i.tone] || TONE.info}`}>{i.status}</span>
          </button>
        ))}
      </div>
      {items.length > shown.length && (
        <CardFooter>Showing {shown.length} of {items.length}</CardFooter>
      )}
    </CardShell>
  );
}

// ─── The week ahead ─────────────────────────────────────────────────────────

// Leave has a tint of its own, so a day off never reads as a day offsite.
const OUTLINE = { home: 'border-blue-200 bg-blue-50', office: 'border-emerald-200 bg-emerald-50', leave: 'border-violet-200 bg-violet-50', offsite: 'border-amber-200 bg-amber-50' };

// `stacked` lays the five days out as rows, for a narrow column such as the
// engineer's home page, where five tiles side by side break words mid-way.
export function WeekAheadCard({ onNavigate, className = '', stacked = false }) {
  const data = useHome('home/week-ahead');
  if (!data?.days?.length) return null;
  return (
    <CardShell title="Week ahead" className={className}
      action={<button type="button" onClick={() => onNavigate && onNavigate('my-bookings')} className={HEADER_LINK}>Plan my week</button>}>
      <div className={`p-3 grid gap-1.5 ${stacked ? 'grid-cols-1' : 'grid-cols-5'}`}>
        {data.days.map(d => (
          <div key={d.date} className={`wk deep-tint flex border rounded p-1.5 min-w-0 ${stacked ? 'flex-row gap-2' : 'flex-col'} ${d.bank_holiday ? 'border-gray-200 bg-gray-50' : OUTLINE[d.where?.code] || 'border-gray-200 bg-white'}`}>
            <div className={stacked ? 'w-12 flex-shrink-0' : ''}>
              <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">{fmtDay(d.date, { weekday: 'short' })}</div>
              <div className="text-[12px] font-bold text-navy leading-tight whitespace-nowrap">{fmtDay(d.date, { day: 'numeric', month: 'short' })}</div>
            </div>
            <div className={`min-w-0 ${stacked ? 'flex-1' : 'flex-1 flex flex-col'}`}>
            <div className={`text-[11px] leading-snug ${stacked ? '' : 'mt-1 min-h-[2.2em]'}`}>
              {d.bank_holiday ? (
                <span className="inline-flex items-center gap-1 text-gray-600" title={d.bank_holiday}><LineIcon name="flag" className="w-3 h-3" />{d.bank_holiday}</span>
              ) : d.where ? (
                <span className="block text-gray-700" title={[d.where.label, d.where.detail].filter(Boolean).join(': ')}>
                  <span className="inline-flex items-center gap-1 font-semibold"><WhereIcon code={d.where.code} fallback={d.where.icon} className="w-3 h-3" />{d.where.short_label || d.where.label}</span>
                  {/* "WH-12 · Wilmore House": each part on a line of its own, so a
                      wrap never strands the dot at a line end. */}
                  {d.where.detail && String(d.where.detail).split(' · ').map((part, k) => (
                    <span key={k} className="block text-[10px] text-gray-600 break-words text-balance">{part}</span>
                  ))}
                </span>
              ) : (
                <span className="text-gray-500 italic">Not set</span>
              )}
            </div>
            {d.bookings.map((b, i) => (
              <div key={i} className="bk mt-1 flex items-center gap-1 text-[10px] font-semibold text-blue-700 bg-blue-50 rounded px-1 py-px min-w-0" title={`${b.name}${b.slot !== 'full' ? ` (${b.slot})` : ''}`}>
                <LineIcon name={b.type === 'van' ? 'van' : 'desk'} className="w-3 h-3" />
                {/* "Van 3 (Transit Custom)" shows as "Van 3": the model is in the
                    tooltip, so the chip stays one line in a narrow day tile. */}
                <span className="truncate">{(b.name || '').replace(/\s*\(.+\)$/, '')}</span>
              </div>
            ))}
            {d.team_off.length > 0 && (
              <div className="mt-1.5 flex items-center gap-0.5 flex-wrap" title={`Off: ${d.team_off.map(p => p.name).join(', ')}`}>
                {d.team_off.slice(0, 3).map(p => (
                  <Avatar key={p.id} as="span" personId={p.id}
                    className="w-4 h-4 rounded-full flex items-center justify-center text-white font-bold text-[9px]"
                    style={{ backgroundColor: p.avatar_color || '#4B5563' }}>
                    {(p.name || '?')[0]}
                  </Avatar>
                ))}
                <span className="text-[10px] text-gray-600">{d.team_off.length > 3 ? `+${d.team_off.length - 3} ` : ''}off</span>
              </div>
            )}
            </div>
          </div>
        ))}
      </div>
      {data.team_size > 0 && (
        <div className="px-3 pb-2 -mt-1 text-[10px] text-gray-500">Faces are teammates who are off that day. Hover for names.</div>
      )}
    </CardShell>
  );
}

// ─── My team today (line managers) ───────────────────────────────────────────

export function TeamTodayCard({ onNavigate }) {
  const data = useHome('home/team');
  if (!data?.has_reports) return null;
  const people = data.people || [];
  const tally = {};
  for (const p of people) {
    const k = p.where?.short_label || 'Not set';
    tally[k] = (tally[k] || 0) + 1;
  }
  const waiting = people.reduce((s, p) => s + (p.leave_waiting || 0), 0);
  const shortOpen = people.filter(p => p.time?.open_week_short);

  return (
    // Other people, not you: a section of its own rather than one more card
    // under My Space.
    <div className="mb-6">
    <h2 className={SECTION_HEADING}>My Team</h2>
    <CardShell
      title={<>My team today <span className="text-gray-400 font-normal">({people.length})</span></>}
      action={<button type="button" onClick={() => onNavigate && onNavigate('leave-calendar')} className={HEADER_LINK}>Leave calendar</button>}>
      <div className="px-3 py-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600 border-b border-gray-100">
        {Object.entries(tally).map(([k, n]) => <span key={k}><span className="font-bold text-navy">{n}</span> {k}</span>)}
        {waiting > 0 && (
          <button type="button" onClick={() => onNavigate && onNavigate('approvals', 'leave')} className="font-semibold text-red-600 hover:underline">
            {plural(waiting, 'leave request')} to decide
          </button>
        )}
        {shortOpen.length > 0 && (
          <span className="font-semibold text-amber-700">{plural(shortOpen.length, 'timesheet')} short before the week closes</span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr>
            <th className="text-left px-3 py-1 text-[10px] uppercase tracking-wide text-gray-500 font-bold bg-gray-50">Person</th>
            <th className="text-left px-2 py-1 text-[10px] uppercase tracking-wide text-gray-500 font-bold bg-gray-50">Today</th>
            {data.can_see_time && <th className="text-left px-2 py-1 text-[10px] uppercase tracking-wide text-gray-500 font-bold bg-gray-50">Time this week</th>}
            <th className="text-left px-2 py-1 text-[10px] uppercase tracking-wide text-gray-500 font-bold bg-gray-50">Needs you</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {people.map(p => {
              const t = p.time;
              const thisShort = t?.tracked && t.this_week.short_by > 0;
              return (
                <tr key={p.id}>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <Avatar as="span" personId={p.id}
                        className="w-5 h-5 rounded-full flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0"
                        style={{ backgroundColor: p.avatar_color || '#4B5563' }}>
                        {(p.name || '?')[0]}
                      </Avatar>
                      <span className="min-w-0">
                        <span className="block text-[11px] font-semibold text-navy truncate">{p.name}</span>
                        {p.designation && <span className="block text-[10px] text-gray-500 truncate">{p.designation}</span>}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-[11px] text-gray-700 whitespace-nowrap" title={p.where?.detail || ''}>
                    {p.where ? <span className="inline-flex items-center gap-1"><WhereIcon code={p.where.code} fallback={p.where.icon} className="w-3 h-3 text-gray-500" />{p.where.short_label || p.where.label}</span> : <span className="text-gray-500 italic">Not set</span>}
                  </td>
                  {data.can_see_time && (
                    <td className="px-2 py-1.5 text-[11px] whitespace-nowrap">
                      {!t || !t.tracked ? <span className="text-gray-500">Not tracked</span> : (
                        <>
                          {t.this_week.expected_to_date > 0 || t.this_week.logged > 0 ? (
                            <span className={thisShort ? 'text-amber-700 font-semibold' : 'text-gray-700'}>
                              {h(t.this_week.logged)} of {h(t.this_week.expected_to_date)} so far
                            </span>
                          ) : <span className="text-gray-500">Nothing expected yet</span>}
                          {t.open_week_short && (
                            <span className="block text-[10px] text-red-700 font-semibold">
                              Week of {fmtDay(t.open_week_short.week_start, { day: 'numeric', month: 'short' })} is {h(t.open_week_short.short_by)} short, closes {t.open_week_short.closes_today ? 'tonight' : fmtDay(t.open_week_short.closes_on)}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                  )}
                  <td className="px-2 py-1.5 text-[11px]">
                    {p.leave_waiting > 0 ? (
                      <button type="button" onClick={() => onNavigate && onNavigate('approvals', 'leave')}
                        className="px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-semibold hover:underline">
                        {plural(p.leave_waiting, 'leave request')}
                      </button>
                    ) : <span className="text-gray-400">–</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!data.can_see_time && (
        <div className="px-3 py-1.5 text-[10px] text-gray-500 border-t border-gray-100">
          Booked hours are not shown: seeing other people&rsquo;s time is a separate permission, set in Roles &amp; Permissions.
        </div>
      )}
    </CardShell>
    </div>
  );
}
